import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listActiveBroadcasts, LiveSession } from '../services/liveSessionService';
import {
  getLiveAlgorithm, DEFAULT_ALGORITHM_ID, LiveFeedLocation,
} from '../services/liveFeedAlgorithms';

/**
 * useLiveFeed
 *
 * Single source of truth for "who's broadcasting live right now",
 * shared between the Live directory overlay and the TikTok-style
 * swipe-between-broadcasts control inside the viewer.  Polls the
 * backend every `intervalMs`, applies the selected algorithm, and
 * returns the sorted list plus helpers for next/previous neighbour
 * navigation.
 *
 * The hook is safe to mount multiple times — each instance polls
 * independently, which is intentional so the viewer can keep a
 * "frozen" snapshot of the feed (e.g. while mid-watch) without
 * fighting with a fresher snapshot driving the directory list.
 */

export interface UseLiveFeedOptions {
  algorithmId?: string;
  viewerLocation?: LiveFeedLocation | null;
  /** Poll cadence in ms.  Defaults to 8s. */
  intervalMs?: number;
  /** If false the hook never polls — used while viewing so the
      next/prev list stays stable during a session. */
  enabled?: boolean;
  /** Refresh once immediately on mount regardless of `enabled`. */
  eagerRefresh?: boolean;
  /** Connected viewer wallet. Group-restricted live sessions are only
      returned when this wallet is a member; public ones always show. */
  viewerWallet?: string | null;
}

export interface UseLiveFeedResult {
  sessions: LiveSession[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Helper: given a wallet, what comes next in the feed? */
  neighbor: (wallet: string, direction: 'next' | 'prev') => LiveSession | null;
}

const DEFAULT_INTERVAL_MS = 8_000;

export function useLiveFeed(opts: UseLiveFeedOptions = {}): UseLiveFeedResult {
  const {
    algorithmId   = DEFAULT_ALGORITHM_ID,
    viewerLocation = null,
    intervalMs    = DEFAULT_INTERVAL_MS,
    enabled       = true,
    eagerRefresh  = true,
    viewerWallet  = null,
  } = opts;

  const [raw, setRaw]         = useState<LiveSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // Stable seed for the `random` algorithm.  We mint one on mount so
  // repeated sort() calls during a single session keep the same order
  // (otherwise swiping "next → back → next" would land on different
  // broadcasters).  A user-initiated "reshuffle" can bump this.
  const seedRef = useRef<number>(Math.floor(Math.random() * 0xFFFFFFFF));

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Pass the viewer wallet as BOTH the owner-context and viewer-scope
      // arg so the backend returns our own broadcasts plus any public /
      // member-visible group-restricted sessions.
      const next = await listActiveBroadcasts(viewerWallet || undefined, viewerWallet || undefined);
      setRaw(next);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load live broadcasts');
    } finally {
      setLoading(false);
    }
  }, [viewerWallet]);

  // Eager first fetch, then interval polling while enabled.
  useEffect(() => {
    let cancelled = false;
    if (eagerRefresh || enabled) {
      refresh();
    }
    if (!enabled) return;
    const id = setInterval(() => { if (!cancelled) refresh(); }, intervalMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, intervalMs, refresh, eagerRefresh]);

  const sessions = useMemo<LiveSession[]>(() => {
    const algo = getLiveAlgorithm(algorithmId) || getLiveAlgorithm(DEFAULT_ALGORITHM_ID)!;
    return algo.sort({ sessions: raw, viewerLocation, seed: seedRef.current });
  }, [raw, algorithmId, viewerLocation]);

  const neighbor = useCallback(
    (wallet: string, direction: 'next' | 'prev'): LiveSession | null => {
      if (sessions.length === 0) return null;
      // Match either the operator/payer wallet OR the on-air broadcaster
      // identity so the feed neighbour lookup works for both human and
      // agent broadcasts. The previous `(s.wallet || s.broadcasterId)`
      // expression always preferred `s.wallet`, which made it impossible
      // to navigate the feed when starting from an agent address.
      const idx = sessions.findIndex(s => s.wallet === wallet || s.broadcasterId === wallet);
      if (idx === -1) return direction === 'next' ? sessions[0] : sessions[sessions.length - 1];
      // Wrap around so the feed feels infinite (TikTok-style).
      const nextIdx = direction === 'next'
        ? (idx + 1) % sessions.length
        : (idx - 1 + sessions.length) % sessions.length;
      if (nextIdx === idx) return null;
      return sessions[nextIdx];
    },
    [sessions],
  );

  return { sessions, loading, error, refresh, neighbor };
}
